import { CommonModule } from "@angular/common";
import { Component, Input, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import {
  DxDataGridComponent,
  DxDataGridModule,
  DxTextBoxComponent,
  DxTextBoxModule,
  DxValidatorModule,
  DxValidationGroupComponent,
  DxValidationGroupModule,
} from "devextreme-angular";
import { AppGlobals } from "src/app/app.globals";
import { GlobalPopupService } from "../generic-popup/presets/global-popup/global-popup.service";
import { GpdButtonComponent } from "../gpd-button/gpd-button.component";
import { SvgIconComponent } from "../svg-icon/svg-icon.component";

export interface TagCatalogEntry {
  id: number;
  name: string;
  /** System tag: shown, but edit and delete are disabled. */
  locked?: boolean;
}

type ApiResult = { hasError?: boolean; feedback?: string };

/** What a tag catalog page plugs in: how its tags are loaded, saved and deleted. */
export interface TagCatalogApi {
  load(): Promise<ApiResult & { tags?: TagCatalogEntry[] }>;
  /** `id` is null for a new tag. */
  save(id: number | null, name: string): Promise<ApiResult>;
  remove(tag: TagCatalogEntry): Promise<ApiResult>;
}

/**
 * Admin ▸ Tags catalog UI shared by the Aufgaben and Kontakte tabs (#4808, #797): header with
 * "Add", one inline form for create and rename, searchable table with edit/delete actions.
 */
@Component({
  selector: "app-tag-catalog",
  imports: [
    CommonModule,
    DxDataGridModule,
    DxTextBoxModule,
    DxValidatorModule,
    DxValidationGroupModule,
    GpdButtonComponent,
    SvgIconComponent,
  ],
  templateUrl: "./tag-catalog.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./tag-catalog.component.css"],
})
export class TagCatalogComponent implements OnInit {
  @Input({ required: true }) api!: TagCatalogApi;
  @Input() heading = "";
  @Input() description = "";

  // Wording, so other catalogs (e.g. Admin ▸ Gesellschaften ▸ Portfoliozuordnungen) can reuse the UI.
  @Input() namePlaceholder = $localize`:@@taskTagNamePlaceholder:Enter tag name`;
  @Input() duplicateMessage = $localize`:@@tagNameExists:A tag with this name already exists`;
  @Input() emptyText = $localize`:@@noTagsYet: No tags yet. Use "Add" to create the first one. `;
  @Input() deleteTitle = $localize`:@@deleteTagTitle:Delete tag`;
  /** The question in the delete popup; `name` is already HTML-escaped. */
  @Input() deleteQuestion = (name: string) =>
    $localize`:@@deleteTaskTagConfirm:Are you sure you want to delete the tag <b>${name}:name:</b>?`;

  @ViewChild("tagsGrid") tagsGrid?: DxDataGridComponent;
  @ViewChild("nameBox") nameBox?: DxTextBoxComponent;
  @ViewChild("formGroup") formGroup?: DxValidationGroupComponent;

  tags: TagCatalogEntry[] = [];
  loaded = false;

  /** Tag being created (id null) or renamed; null while the table is shown. */
  editing: { id: number | null; name: string } | null = null;

  constructor(private popupService: GlobalPopupService) {}

  ngOnInit(): void {
    this.loadTags();
  }

  async loadTags() {
    try {
      const result = await this.api.load();
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }
      this.tags = result.tags ?? [];
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    } finally {
      this.loaded = true;
    }
  }

  startCreate() {
    this.openForm({ id: null, name: "" });
  }

  startEdit(tag: TagCatalogEntry) {
    this.openForm({ id: tag.id, name: tag.name });
  }

  private openForm(value: { id: number | null; name: string }) {
    this.editing = value;
    setTimeout(() => this.nameBox?.instance.focus(), 50);
  }

  cancel() {
    this.editing = null;
  }

  async confirm() {
    if (!this.editing || !this.formGroup?.instance.validate().isValid) return;
    try {
      const result = await this.api.save(this.editing.id, this.editing.name.trim());
      if (result.hasError) {
        AppGlobals.ShowErrorMessage(result.feedback);
        return;
      }
      this.editing = null;
      await this.loadTags();
    } catch (error) {
      AppGlobals.ShowErrorMessage(error);
    }
  }

  confirmDelete(tag: TagCatalogEntry) {
    const name = escapeHtml(tag.name);
    this.popupService.showDeletePopup({
      title: this.deleteTitle,
      content:
        this.deleteQuestion(name) +
        "<br>" +
        $localize`:@@actionCannotBeUndone:This action cannot be undone.`,
      confirmText: $localize`:@@yesDelete:Yes, delete`,
      onConfirm: async () => {
        const result = await this.api.remove(tag);
        if (result.hasError) {
          AppGlobals.ShowErrorMessage(result.feedback);
          return;
        }
        await this.loadTags();
      },
    });
  }

  search(value: string) {
    this.tagsGrid?.instance.searchByText(value ?? "");
  }

  /** Client-side duplicate check so the user sees it inline before the API rejects it. */
  isNameUnique = (e: { value: string }) => {
    const name = (e.value ?? "").trim().toLowerCase();
    return !this.tags.some((t) => t.id !== this.editing?.id && t.name.toLowerCase() === name);
  };
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}
